import React from 'react'
import Nav from '../../src/components/Nav'
import { urls } from '../../src/utils/config'
import Footer from '../../src/components/Footer'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Divider, Grid, Link, Button } from '@material-ui/core'
import HelpSection from '../../src/components/Help/HelpSection'
import JoinCommunitySection from '../../src/components/Footer/JoinCommunitySection'
import TopicSideBar from '../../src/components/Academy/Topic/TopicSideBar'

import { getLessons, getTopic, getTopics } from '../../src/services/academy'
import { Lesson as ILesson, Topic as ITopic } from '../../types/academy'
import { GetServerSideProps } from 'next'
import LessonGrid from '../../src/components/Academy/Lesson/LessonGrid'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '1200px',
    margin: 'auto',
  },
  contentSection: {
    margin: '0',
    padding: '20px',
  },
  title: {
    textAlign: 'left',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '48px',
      maxWidth: '100%',
      margin: '0 auto 0 auto',
    },
  },
  subtitle: {
    color: 'black',
    marginTop: '20px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  heroTextSection: {
    width: '100%',
  },
  section: {},
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '40px 0',
  },
  breadCrumbs: {
    margin: ' 0 0 2rem 0',
    padding: '.5rem .5rem .5rem 1.5rem',
    position: 'relative',
    fontSize: '.8rem',
    '&::before': {
      position: 'absolute',
      content: '""',
      height: '100%',
      top: 0,
      left: 0,
      width: '0.5rem',
      background: theme.palette.primary.main,
      borderRadius: '0.3rem',
    },
  },
  p: {
    fontSize: '.8rem',
    fontWeight: 'bold',
  },

  sideBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
  topicLegend: {
    display: 'flex',
  },
}))

interface Props {
  topics: ITopic[]
  lessons: ILesson[]
  topic: ITopic
}

function Lesson({ topics, lessons, topic }: Props): JSX.Element {
  const classes = useStyles({})

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.home,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters | Lessons`}
            description={`Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`}
            openGraph={{
              url: `https://${process.env.NEXT_PUBLIC_APP_URL}/lessons`,
              title: `Lesson`,
              description: `Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`,
              images: [
                {
                  url: `${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/lessons.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/lessons.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav dark />
          <div className={classes.root}>
            <div className={classes.contentSection}>
              <Grid container spacing={8}>
                <Grid item md={3} className={classes.sideBar}>
                  <TopicSideBar topics={topics} currentTopic={''} />
                </Grid>
                <Grid item md={9}>
                  <div className={classes.heroContent}>
                    <div className={classes.heroTextSection}>
                      <Typography variant="h1" className={classes.title}>
                        All {topic.title ? topic.title : ''} lessons.
                      </Typography>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        {` Browse through many our ${topic.title} lessons. We have prepared lessons on ${topic.title} to get you up to speed!`}
                      </Typography>
                      <Divider className={classes.divider} />
                    </div>
                  </div>
                  <div>
                    {lessons.length > 0 && (
                      <div>
                        <LessonGrid lessons={lessons} md={4} lg={4} />
                      </div>
                    )}
                    {lessons.length === 0 && topic?.title?.length > 0 ? (
                      <div>
                        <Link href="/lesson" underline="always">
                          <Typography variant="subtitle2" className={classes.subtitle}>
                            No lessons in this topic, view all lessons.
                          </Typography>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </Grid>
              </Grid>
            </div>
          </div>
          <HelpSection />
          <JoinCommunitySection />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  try {
    let topicSlug = ''
    topicSlug = (query.topic ? query.topic : '') as string
    let topic = {}
    let lessons = []
    if (topicSlug.length > 0) {
      topic = await getTopic(topicSlug)
      // filter out lessons that have topics
      lessons = await getLessons({ 'topics.slug': topicSlug })
    } else {
      lessons = await getLessons({
        _limit: 10,
      })
    }
    const topics = await getTopics()
    return {
      props: {
        lessons,
        topics,
        topic,
      },
    }
  } catch (e) {
    console.error(e.message)
    return {
      notFound: true,
    }
  }
}

export default Lesson
